import React, { useContext } from 'react'

import { Login, UserTypes } from 'src/model'

import AppInfoContext from 'src/provider/state-manager/appInfoProvider'
import VisibilityContext from 'src/provider/state-manager/visibilityProvider'

import helpers from 'src/utils/helpers'
import { catchErrMsg, erroMessage } from 'src/utils/utility'

import customerCaller from '../../interceptor/customerInterceptor'

const AuthApiContext = React.createContext<any>(null)

export const AuthApiProvider = (props: any) => {

    const { loader, notifier } = useContext(VisibilityContext)
    const { setInfoProperty } = useContext(AppInfoContext)

    async function getInstitutionById(id: number) {
        if (!id) return false

        try {
            // institutionData isn't in localStorage yet at this point in the
            // login flow, so customerInterceptor can't auto-inject the
            // header — pass it explicitly, same as the login/OTP calls.
            const { data } = await customerCaller.get(`admin/Institutions/Profile`, {
                headers: { 'X-Institution-Id': `${id}` },
            })

            await setInfoProperty('institutionData', data)
            return data
        }
        catch (err) {
            notifier.show(erroMessage(catchErrMsg(err)))
            return null
        }
    }

    // LoginOtp's success response carries no user/session data (just
    // messageCode) — the real session is the HttpOnly SelfServiceAuth cookie
    // the server sets, sent automatically by customerCaller/apiCaller via
    // withCredentials. So we fetch the profile ourselves and only store a
    // truthy sentinel in adminAuthToken to gate the SPA's own isAuth checks.
    async function applyLoginResult(ibInstitutionCode: string | number) {
        const { data: profile } = await customerCaller.get(`admin/Institutions/AdminProfile`)
        const info: any = { ...profile, ibInstitutionCode }

        let qoreStaffType

        if (['SA', 'Qore User', 'QoreUser', 'QoreDeveloper'].includes(info.role)) {
            qoreStaffType = info.role
            info.role = 'SA'
        }

        await setInfoProperty('adminAuthToken', 'authenticated')

        if (info.role != 'SA' && Number(info.ibInstitutionCode) !== 0) {
            const institution = await getInstitutionById(Number(info.ibInstitutionCode))
            if (!institution) {
                await setInfoProperty('adminAuthToken', '')
                return false
            }
        }

        await Promise.all([
            setInfoProperty('role', info.role),
            setInfoProperty('userType', info.role),
            setInfoProperty('sidebarMenuType', [UserTypes.ADMIN, UserTypes.USER].includes(info.role) ? 'appConfig' : 'institutions'),
            setInfoProperty('userData', info),
            qoreStaffType && setInfoProperty('qoreStaffType', qoreStaffType)
        ])

        return info
    }

    // ---- login ----
    // Login only validates credentials and triggers an OTP to the user's
    // email — it doesn't return a session. validateLoginOtp completes the
    // login and returns the auth token, mirroring the forgot-password OTP
    // pair below.
    //
    // Both endpoints use a `messageCode` discriminator instead of the
    // `responseCode`/`isSuccessful` envelope used elsewhere — and the failure
    // codes come back on a non-2xx status (e.g. INCORRECT_CREDENTIALS is a
    // 400), so it has to be checked in both the success and catch paths:
    //   Login:    OTP_SENT (200) | INCORRECT_CREDENTIALS (400)
    //   LoginOtp: LOGIN_SUCCESS (200) | anything else = invalid/expired OTP

    function loginFailureMessage(messageCode?: string) {
        if (messageCode === 'INCORRECT_CREDENTIALS') return 'Incorrect username or password.'
        return 'Something went wrong, Please try again later. If problem persist kindly contact our support.'
    }

    async function login(payload: Login) {
        try {
            loader(true)
            const { data } = await customerCaller.post(`admin/AdminProfile/Login`, {
                username: payload.userID,
                password: helpers.hashString(payload.password)
            }, {
                headers: { 'X-Institution-Id': payload.ibInstitutionCode },
            })

            if (data.messageCode !== 'OTP_SENT') {
                notifier.show(loginFailureMessage(data.messageCode))
                return false
            }

            return data
        }
        catch (err: any) {
            // INCORRECT_CREDENTIALS comes back as a non-2xx status, so axios
            // throws instead of resolving — recover the messageCode from the
            // error response body before falling back to a generic message.
            const messageCode = err?.response?.data?.messageCode
            if (!messageCode) {
                notifier.show(erroMessage(catchErrMsg(err)))
                return null
            }
            notifier.show(loginFailureMessage(messageCode))
            return false
        }
        finally {
            loader(false)
        }
    }

    async function validateLoginOtp(ibInstitutionCode: string | number, otp: string) {
        try {
            loader(true)
            const { data } = await customerCaller.post(`admin/AdminProfile/LoginOtp`, {
                otp: helpers.hashString(otp)
            }, {
                headers: { 'X-Institution-Id': ibInstitutionCode },
            })

            if (data.messageCode !== 'LOGIN_SUCCESS') {
                notifier.show('Invalid or expired code. Please try again.')
                return false
            }

            return await applyLoginResult(ibInstitutionCode)
        }
        catch (err: any) {
            // Same non-2xx-on-failure shape as login() above.
            const messageCode = err?.response?.data?.messageCode
            if (!messageCode) {
                notifier.show(erroMessage(catchErrMsg(err)))
                return null
            }
            notifier.show('Invalid or expired code. Please try again.')
            return false
        }
        finally {
            loader(false)
        }
    }

    // Only the server round-trip — clearing local session state and
    // redirecting is handled by AppInfoContext's logout(), same as the v1
    // logout button, so both stay in sync.
    async function logout() {
        try {
            await customerCaller.post(`admin/AdminProfile/Logout`)
        }
        catch (err) {
            // best-effort — proceed to clear the local session regardless
        }
    }

    // ---- forgot password ----
    // Same messageCode-discriminated shape as login above, and the same
    // pre-auth explicit X-Institution-Id header (institutionData isn't in
    // localStorage yet). Unlike login's two-step OTP dance, ResetPassword
    // validates the OTP and sets the new password in one call — there's no
    // separate "validate OTP" step, so the OTP is held client-side until the
    // user has also entered a new password, then sent together here.
    //   ForgotPassword: OTP_SENT (200) | anything else = unrecognized username
    //   ResetPassword:  PASSWORD_UPDATED (200) | PASSWORDS_DO_NOT_MATCH | OTP_IS_INVALID

    function resetPasswordFailureMessage(messageCode?: string) {
        if (messageCode === 'PASSWORDS_DO_NOT_MATCH') return "The passwords don't match."
        if (messageCode === 'OTP_IS_INVALID') return 'Invalid or expired code. Please try again.'
        return 'Something went wrong, Please try again later. If problem persist kindly contact our support.'
    }

    async function forgotPassword(ibInstitutionCode: string | number, username: string) {
        try {
            loader(true)
            const { data } = await customerCaller.post(`admin/AdminProfile/ForgotPassword`, {
                username
            }, {
                headers: { 'X-Institution-Id': ibInstitutionCode },
            })

            if (data.messageCode !== 'OTP_SENT') {
                notifier.show('Something went wrong, Please try again later. If problem persist kindly contact our support.')
                return false
            }

            return data
        }
        catch (err: any) {
            const messageCode = err?.response?.data?.messageCode
            if (!messageCode) {
                notifier.show(erroMessage(catchErrMsg(err)))
                return null
            }
            notifier.show('Something went wrong, Please try again later. If problem persist kindly contact our support.')
            return false
        }
        finally {
            loader(false)
        }
    }

    // Returns the response messageCode so the caller can tell PASSWORD_UPDATED
    // apart from PASSWORDS_DO_NOT_MATCH / OTP_IS_INVALID (the latter sends the
    // user back to re-enter the OTP).
    async function resetPassword(ibInstitutionCode: string | number, otp: string, newPassword: string, confirmNewPassword: string) {
        try {
            loader(true)
            const { data } = await customerCaller.post(`admin/AdminProfile/ResetPassword`, {
                otp: helpers.hashString(otp),
                newPassword: helpers.hashString(newPassword),
                confirmNewPassword: helpers.hashString(confirmNewPassword)
            }, {
                headers: { 'X-Institution-Id': ibInstitutionCode },
            })

            if (data.messageCode !== 'PASSWORD_UPDATED') {
                notifier.show(resetPasswordFailureMessage(data.messageCode))
                return data.messageCode
            }

            notifier.show('Password Successfully Reset', null, 'success')
            return data.messageCode
        }
        catch (err: any) {
            const messageCode = err?.response?.data?.messageCode
            if (!messageCode) {
                notifier.show(erroMessage(catchErrMsg(err)))
                return null
            }
            notifier.show(resetPasswordFailureMessage(messageCode))
            return messageCode
        }
        finally {
            loader(false)
        }
    }

    return (
        <AuthApiContext.Provider
            value={{
                login,
                validateLoginOtp,
                logout,
                forgotPassword,
                resetPassword,
            }}
        >
            {props.children}
        </AuthApiContext.Provider>
    )
}

export default AuthApiContext